/**
 * ISEECalcChip — inline "What will *I* pay?" chip for Italian degrees.
 *
 * Italy's public universities operate a means-tested (ISEE — Indicatore
 * della Situazione Economica Equivalente) fee model. Households under
 * €22,000 ISEE pay €0 ("No Tax Area"); everyone above the threshold
 * pays a scaled fraction of the uni's max band up to the ceiling for
 * ISEE > €90,000.
 *
 * This chip renders a compact estimator so a family can see their real
 * annual fee for the specific uni they're looking at, right where the
 * headline sticker price sits.
 */
import { useMemo, useState } from "react";
import { Calculator } from "lucide-react";

const ISEE_BANDS = [
  { label: "ISEE < €22,000",       min: 0,      max: 22000,   pct: 0.00 },
  { label: "€22,000 – €30,000",    min: 22000,  max: 30000,   pct: 0.30 },
  { label: "€30,000 – €45,000",    min: 30000,  max: 45000,   pct: 0.55 },
  { label: "€45,000 – €60,000",    min: 45000,  max: 60000,   pct: 0.75 },
  { label: "€60,000 – €90,000",    min: 60000,  max: 90000,   pct: 0.90 },
  { label: "ISEE > €90,000 / N.D.", min: 90000, max: Infinity, pct: 1.00 },
];

const EUR_TO_USD = 1.08;

export default function ISEECalcChip({ maxTuitionUsd = 0, isPrivate = false }) {
  const [open, setOpen] = useState(false);
  const [isee, setIsee] = useState(30000);

  const maxEur = useMemo(() => Math.round((maxTuitionUsd || 0) / EUR_TO_USD), [maxTuitionUsd]);
  const band = useMemo(() => ISEE_BANDS.find((b) => isee >= b.min && isee < b.max) || ISEE_BANDS[ISEE_BANDS.length - 1], [isee]);
  const feeEur = useMemo(() => Math.round(maxEur * band.pct), [maxEur, band]);
  const feeUsd = Math.round(feeEur * EUR_TO_USD);

  if (isPrivate) {
    // Private unis (Bocconi, LUISS, Cattolica) don't use ISEE — flat rate.
    return null;
  }

  return (
    <div className="mt-3" data-testid="isee-calc-chip-root">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="isee-calc-chip-open"
          className="inline-flex items-center gap-2 rounded-full border border-emerald-400/60 bg-emerald-50 px-3 py-1.5 text-[12px] font-medium text-emerald-900 transition hover:bg-emerald-100 hover:border-emerald-500"
        >
          <Calculator className="h-3.5 w-3.5" />
          What will <em className="not-italic font-semibold underline decoration-emerald-500 decoration-2 underline-offset-2">I</em> pay?
          <span className="text-emerald-700 text-[10px] font-underground-sc tracking-wider">ISEE Calc</span>
        </button>
      ) : (
        <div
          data-testid="isee-calc-chip-panel"
          className="rounded-lg border border-emerald-300 bg-emerald-50/50 p-4 max-w-xl"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-underground-sc tracking-wider text-emerald-800">
              Italian ISEE estimator
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="isee-calc-chip-close"
              className="text-[11px] text-emerald-700 hover:underline"
            >
              Close
            </button>
          </div>

          <label className="block text-sm text-stone-800 mb-1">
            Your household ISEE (annual, in €):
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="120000"
              step="1000"
              value={isee}
              onChange={(e) => setIsee(Number(e.target.value))}
              data-testid="isee-calc-chip-slider"
              className="flex-1 accent-emerald-600"
            />
            <div className="w-24 text-right font-mono text-sm text-stone-900">
              €{isee.toLocaleString("it-IT")}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-md bg-white/70 p-3 border border-emerald-200">
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Your band</div>
              <div className="mt-1 font-semibold text-stone-900">{band.label}</div>
              <div className="mt-1 text-[11px] text-stone-600">
                Applies {Math.round(band.pct * 100)}% of the uni's max tariff
              </div>
            </div>
            <div className="rounded-md bg-emerald-600 p-3 text-white">
              <div className="text-[10px] font-underground-sc tracking-wider text-emerald-100">
                Your estimated fee
              </div>
              <div className="mt-1 font-bold text-2xl leading-none">
                €{feeEur.toLocaleString("it-IT")}
              </div>
              <div className="mt-1 text-[11px] text-emerald-100">
                ~ ${feeUsd.toLocaleString("en-US")} / year
              </div>
            </div>
          </div>

          <div className="mt-3 text-[11px] text-stone-600 leading-relaxed">
            <strong>Note:</strong> ISEE (Indicatore della Situazione Economica Equivalente) is the means-test used by every Italian public university. Households under €22,000 pay <strong>€0</strong> ("No Tax Area", Art. 1 co. 252 L. 232/2016). Bands above are typical — each uni sets exact percentages in its Regolamento Tasse.
          </div>
        </div>
      )}
    </div>
  );
}
